import { Component, Input, OnInit, Inject, Optional, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { DOCUMENT, AsyncPipe, TitleCasePipe, CurrencyPipe } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { QrCodeComponent } from 'ng-qrcode';
import { DunaLoginComponent } from '../duna-login/duna-login.component';
import { DunaUserParticipateComponent } from '../duna-user-participate/duna-user-participate.component';
import { EventDTO } from '../../../dto/event/event.dto';
import { RoomDTO } from '../../../dto/location/room.dto';
import { MODAL_DATA, MODAL_UUID } from '../../dynamic-modal/dynamic-modal.component';
import { DunaEventsService } from '../../../services/duna/duna-events.service';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ModalService } from '../../../services/modal/modal.service';
import { UserService } from '../../../services/user/user.service';
import { PermissionEvaluatorService } from '../../../services/component-permission/permission-evaluator.service';
import { Subject, Observable, BehaviorSubject, of } from 'rxjs';
import { takeUntil, map, take, switchMap, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectUserRoles, selectIsAuthenticated } from '../../../ngrx/auth/auth.selectors';
// Import DTOs for type guards
import { SubTypeDTO } from '../../../dto/subtype/subtype.dto';
import { GoalDTO } from '../../../dto/goal/goal.dto';
import { ModuleDTO } from '../../../dto/module/module.dto';
import { EducationalPathDTO } from '../../../dto/educational-path/educational-path.dto';
import { EventItemDTO } from '../../../dto/event/event-item.dto';
import { EventParticipantDTO } from '../../../dto/event/event-participant.dto';
import { MediaService } from '../../../services/media/media.service';
import { FileItemDTO } from '../../../dto/file-manager/file-item.dto';
import { AuthService } from '../../../services/auth/auth.service';
import { shouldShowEventVisibilityLabel } from '../../../utils/event-visibility-display.util';
import { ParticipateEventContextService } from '../../../services/participate-event-context.service';
import { isAfbudParticipationStatus } from '../../../constants/event-participant-status.utils';

@Component({
  selector: 'app-duna-events-information',
  standalone: true,
  imports: [
    AsyncPipe,
    TitleCasePipe,
    CurrencyPipe,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    QrCodeComponent,
    DunaLoginComponent,
    DunaUserParticipateComponent,
  ],
  templateUrl: './duna-events-information.component.html',
  styleUrl: './duna-events-information.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DunaEventsInformationComponent implements OnInit, OnDestroy {
  @Input() event?: EventDTO;
  isModal = true;

  hasValidData = false;
  isLoading = false;
  error?: string;
  showLoginButton = false;
  
  // Track expanded modules by their ID
  expandedModules: { [key: string]: boolean } = {};
  
  // QR Code properties
  showQRModal = false;
  eventQRData = '';
  linkCopied = false;
  @ViewChild('qrModalOverlay') qrModalOverlay?: ElementRef<HTMLElement>;
  
  // Login component properties
  showLoginComponent = false;

  /** When true (org setting "Vis brugere fra alle lokationer ved øvelser"), show all org participants; otherwise only same location. */
  showAllLocationsUsers = false;
  /** True once we have attempted to load org setting for the event's organization. */
  orgSettingLoaded = false;

  eventFiles: FileItemDTO[] = [];
  eventFolders: FileItemDTO[] = [];
  isLoadingEventFiles = false;
  userCanEditEvent = false;
  private eventFilesFolderStack: Array<{ name: string; obfuscatedName: string }> = [];
  
  private destroy$ = new Subject<void>();
  userRoles$: Observable<string[]>;
  isLoggedIn$: Observable<boolean>;
  private currentUserUuid: string | null = null;
  private canEditEventCache$: Observable<boolean> | null = null;

  constructor(
    @Optional() @Inject(MODAL_DATA) private modalData: any,
    @Optional() @Inject(MODAL_UUID) private modalUuid: string,
    private eventsService: DunaEventsService,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private componentDataSourceService: ComponentDataSourceService,
    private modalService: ModalService,
    private store: Store,
    public userService: UserService,
    private permissionEvaluatorService: PermissionEvaluatorService,
    private renderer: Renderer2,
    private mediaService: MediaService,
    private authService: AuthService,
    private participateEventContext: ParticipateEventContextService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.userRoles$ = this.store.select(selectUserRoles);
    this.isLoggedIn$ = this.store.select(selectIsAuthenticated);
  }

  private getEventGuidFromUrl(): string | null {
    // Check if we're on an event page with a UUID in the path
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const pathSegments = this.router.url.split('/');
    const eventIndex = pathSegments.indexOf('event');
    
    if (eventIndex !== -1 && pathSegments.length > eventIndex + 1) {
      const potentialGuid = pathSegments[eventIndex + 1];
      if (uuidPattern.test(potentialGuid)) {
        return potentialGuid;
      }
    }

    return null;
  }

  ngOnInit() {
    // First try to get the guid from the URL
    const urlGuid = this.getEventGuidFromUrl();
    if (urlGuid) {
      this.isModal = false; // We're on the event page
      this.fetchEventData(urlGuid);
    } else if (this.modalData) {
      this.isModal = true; // We're in a modal
      if (!this.event) {
        if (this.modalData.guid) {
          this.fetchEventData(this.modalData.guid);
        } else if (this.modalData.data) {
          console.warn('DunaEventsInformation: Using legacy event_id instead of guid');
          this.fetchEventData(this.modalData.data);
        }
      }
    } else {
      this.validateEventData();
      this.refreshEventFilesAccess();
    }

    // Subscribe to route changes to update modal state
    this.route.url.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const newUrlGuid = this.getEventGuidFromUrl();
      this.isModal = !newUrlGuid;
      if (newUrlGuid && (!this.event || this.event.guid !== newUrlGuid)) {
        this.fetchEventData(newUrlGuid);
      }
      this.cdr.detectChanges();
    });

    // Debug current user information
    this.debugCurrentUser();

    // Initialize current user UUID
    this.initializeCurrentUserUuid();
    if (this.currentUserUuid) {
      this.refreshEventFilesAccess();
    }
    
    // Monitor authentication state changes for login completion
    this.monitorAuthenticationState();
  }

  private debugCurrentUser(): void {
    const currentUserUuid = this.userService.getCurrentUserUuid();
    
    this.userService.getCurrentUser().subscribe({
      next: (user) => {
      },
      error: (error) => {
        console.error('DunaEventsInformation: Error getting current user:', error);
      }
    });
  }

  private initializeCurrentUserUuid(): void {
    // First try to get UUID from URL (for profile pages)
    this.currentUserUuid = this.userService.getCurrentUserUuid();
    
    // If no UUID from URL, get it from token
    if (!this.currentUserUuid) {
      this.userService.getCurrentUserUuidFromToken().pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (uuid: string | null) => {
          if (uuid) {
            this.currentUserUuid = uuid;
            this.refreshEventFilesAccess();
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          console.error('DunaEventsInformation: Error getting UUID from token:', error);
        }
      });
    }
  }

  private monitorAuthenticationState(): void {
    // Monitor authentication state changes to refresh page after login
    this.store.select(selectIsAuthenticated).pipe(
      takeUntil(this.destroy$),
      filter(isAuthenticated => isAuthenticated && this.showLoginComponent),
      map(() => {
        
        // Refresh the page to ensure all components get updated with new auth state
        // Use a small delay to ensure the login state is fully processed
        setTimeout(() => {
          window.location.reload();
        }, 500);
      })
    ).subscribe();
  }

  ngOnDestroy() {
    this.participateEventContext.setEventGuid(null);
    this.removeQrModalFromBody();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private syncParticipateEventContext(): void {
    if (this.showParticipateForm && this.event?.guid) {
      this.participateEventContext.setEventGuid(this.event.guid);
    } else {
      this.participateEventContext.setEventGuid(null);
    }
  }

  private fetchEventData(guidOrId: string | number) {
    this.isLoading = true;
    this.error = undefined;
    this.hasValidData = false;
    this.cdr.detectChanges();
    

    this.eventsService.getEventByGuid(guidOrId.toString()).subscribe({
      next: (eventData) => {
        this.event = eventData;
        this.isLoading = false;
        // Clear permission cache when event data changes
        this.canEditEventCache$ = null;
        this.orgSettingLoaded = false;
        this.loadOrgSettingForEvent();
        this.validateEventData();
        this.refreshEventFilesAccess();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('DunaEventsInformation: Error fetching event data:', error);
        
        // Check if it's a 403 Forbidden (login required)
        if (error.status === 403 && error.error?.requires_login) {
          this.error = 'Du skal være logget ind for at se denne begivenhed.';
          this.showLoginButton = true;
          this.showLoginComponent = true;
          this.setupEventRedirect();
        } else if (error.status === 404) {
          this.error = 'Begivenheden blev ikke fundet';
          this.showLoginButton = false;
        } else {
          this.error = 'Kunne ikke indlæse begivenhedsdata';
          this.showLoginButton = false;
        }
        
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private validateEventData() {
    
    if (!this.event) {
      console.error('DunaEventsInformation: No event data provided');
      this.error = 'Ingen begivenhedsdata tilgængelig';
      this.hasValidData = false;
      this.cdr.detectChanges();
      return;
    }

    const requiredProperties = [
      'event_id',
      'description',
      'start_date',
      'end_date',
      'event_type',
      'visibility'
    ];

    const missingProperties = requiredProperties.filter(prop => !(prop in this.event!));
    
    if (missingProperties.length > 0) {
      console.error('DunaEventsInformation: Missing required properties:', missingProperties);
      this.error = `Manglende påkrævede egenskaber: ${missingProperties.join(', ')}`;
      this.hasValidData = false;
      this.cdr.detectChanges();
      return;
    }

    if (!this.event.description?.short_description) {
      console.error('DunaEventsInformation: Missing description data');
      this.error = 'Begivenhedsbeskrivelse mangler';
      this.hasValidData = false;
      this.cdr.detectChanges();
      return;
    }

    
    // Load org setting for "show all locations users" when not yet loaded (e.g. when event came via @Input())
    if (!this.orgSettingLoaded && this.event.organization?.id) {
      this.loadOrgSettingForEvent();
    }
    
    // Debug participation status
    this.debugParticipationStatus();
    
    // Load related events for timeline view
    this.loadRelatedEvents();
    
    this.hasValidData = true;
    this.syncParticipateEventContext();
    this.cdr.detectChanges();
  }

  private debugParticipationStatus(): void {
    const currentUserUuid = this.userService.getCurrentUserUuid();
    
    if (this.event?.participants) {
      this.event.participants.forEach((participant, index) => {
      });
    }
    
  }

  get hasItems(): boolean {
    return !!this.event?.items && this.event.items.length > 0;
  }

  get eventItems() {
    return this.event?.items || [];
  }

  /**
   * Capacity helper: prefer manual capacity, otherwise use predefined room capacity.
   */
  get eventCapacity(): number | null {
    const raw = this.event?.manual_location_room_capacity ?? this.event?.predefined_room?.capacity;
    if (raw === undefined || raw === null || !Number.isFinite(raw)) {
      return null;
    }
    return raw;
  }

  get participantCount(): number {
    if (!this.event?.participants) return 0;
    return this.event.participants.filter((p) => this.isOccupyingParticipant(p)).length;
  }

  get remainingSpots(): number | null {
    if (this.eventCapacity === null) return null;
    return Math.max(0, this.eventCapacity - this.participantCount);
  }

  get remainingSpotsLabel(): string {
    if (this.eventCapacity === null) {
      return '';
    }
    const extra = this.participantCount - this.eventCapacity;
    if (extra > 0) {
      return extra === 1 ? '1 over kapacitet' : `${extra} over kapacitet`;
    }
    if (this.remainingSpots === 0) {
      return 'Ingen ledige pladser';
    }
    return `${this.remainingSpots} pladser tilbage`;
  }

  get waitlistCount(): number {
    if (!this.event?.participants?.length) {
      return 0;
    }
    return this.event.participants.filter((p) => {
      const st = p.status != null ? String(p.status).toLowerCase() : '';
      return (st === 'waitlist' || st === 'waitlisted') && p.role !== 'instructor';
    }).length;
  }

  get waitlistCountLabel(): string {
    const n = this.waitlistCount;
    return n === 1 ? '1 på venteliste' : `${n} på venteliste`;
  }

  get cancellationCount(): number {
    if (!this.event?.participants?.length) {
      return 0;
    }
    return this.event.participants.filter((p) => {
      if (p.role === 'instructor') return false;
      return isAfbudParticipationStatus(p.status);
    }).length;
  }

  get cancellationCountLabel(): string {
    const n = this.cancellationCount;
    return n === 1 ? '1 afbud' : `${n} afbud`;
  }

  get participantsSameLocation(): EventParticipantDTO[] {
    return (this.event?.participants_same_location ?? []).filter((p) => this.isOccupyingParticipant(p));
  }

  /**
   * Participants to show in "others participating" section.
   * If org setting "Vis brugere fra alle lokationer ved øvelser" is enabled, returns participants from same organization (excluding current user and instructors).
   * Otherwise returns only participants from the same location as the current user.
   */
  get participantsToShow(): EventParticipantDTO[] {
    if (!this.event) return [];
    if (this.showAllLocationsUsers && this.event.participants?.length) {
      const orgId = this.event.organization?.id ?? null;
      const orgIdNum = orgId != null ? Number(orgId) : null;
      return this.event.participants.filter((p) => {
        if (!this.isOccupyingParticipant(p)) return false;
        if (this.currentUserUuid && p.user?.uuid === this.currentUserUuid) return false;
        if (orgIdNum != null && p.user?.organizations_id != null) {
          return Number(p.user.organizations_id) === orgIdNum;
        }
        return false;
      });
    }
    return this.participantsSameLocation;
  }

  get participantsSectionLabel(): string {
    return this.showAllLocationsUsers
      ? 'Andre fra din organisation har tilmeldt sig'
      : 'Andre fra din lokation har tilmeldt sig';
  }

  /** True when the participant occupies a seat (excludes instructors, waitlist, afbud, not_participant). */
  private isOccupyingParticipant(participant: EventParticipantDTO): boolean {
    if (participant.role === 'instructor') return false;
    const st = participant.status != null ? String(participant.status).toLowerCase() : '';
    return (
      st !== 'waitlist' &&
      st !== 'not_participant' &&
      !isAfbudParticipationStatus(participant.status)
    );
  }

  private loadOrgSettingForEvent(): void {
    if (!this.authService.isAuthenticated) {
      this.showAllLocationsUsers = false;
      this.orgSettingLoaded = true;
      this.cdr.detectChanges();
      return;
    }

    const orgId = this.event?.organization?.id ?? null;
    if (orgId == null) {
      this.orgSettingLoaded = true;
      this.cdr.detectChanges();
      return;
    }
    this.componentDataSourceService.executeDataSource('org_settings', { organizationId: orgId })?.pipe(
      takeUntil(this.destroy$),
      take(1)
    ).subscribe({
      next: (res: { data?: Record<string, unknown> }) => {
        const data = res?.data ?? {};
        const v = data['events_show_all_locations_users'];
        this.showAllLocationsUsers = v === true || v === 'true' || v === 1;
        this.orgSettingLoaded = true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.orgSettingLoaded = true;
        this.cdr.detectChanges();
      }
    });
  }

  get location(): string {
    if (!this.event) return '';
    
    // If we have a predefined location, use that
    if (this.event.predefined_location) {
      let locationText = this.event.predefined_location.locations_name;
      
      // Add room name if available
      if (this.event.predefined_room?.room_name) {
        locationText += `, ${this.event.predefined_room.room_name}`;
      }
      
      return locationText;
    }
    
    // Fall back to manual location
    if (this.event.manual_location) {
      return `${this.event.manual_location}${this.event.manual_location_address ? `, ${this.event.manual_location_address}` : ''}`;
    }
    
    return 'Lokationsinformation ikke tilgængelig';
  }

  get extraRooms(): RoomDTO[] {
    return (this.event?.additional_rooms ?? []).filter(
      (room): room is RoomDTO =>
        'room_name' in room &&
        typeof room.room_name === 'string' &&
        room.room_name.trim().length > 0
    );
  }

  get locationAddress(): string | null {
    if (!this.event?.predefined_location) return null;
    
    // Get address from location's data JSONB field
    const address = this.event.predefined_location.data?.locations_address;
    return address || null;
  }

  getGoogleMapsUrl(address: string): string {
    // Encode the address for use in Google Maps URL
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  openAddressInGoogleMaps(address: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const url = this.getGoogleMapsUrl(address);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  getEventTitle(event: EventDTO): string {
    const shortDesc = event.description?.short_description;
    const longDesc = event.description?.long_description;
    
    // Handle language object format
    if (typeof shortDesc === 'object' && shortDesc?.['da']?.translation_text) {
      return shortDesc['da'].translation_text;
    }
    if (typeof longDesc === 'object' && longDesc?.['da']?.translation_text) {
      return longDesc['da'].translation_text;
    }
    
    // Handle string format
    if (typeof shortDesc === 'string') return shortDesc;
    if (typeof longDesc === 'string') return longDesc;
    
    return 'Ingen titel';
  }

  hasUserParticipated(event: EventDTO): boolean {
    if (!this.currentUserUuid || !event.participants) return false;
    return event.participants.some(participant => participant.user?.uuid === this.currentUserUuid);
  }

  hasUserParticipatedInMainEvent(): boolean {
    if (!this.isSubEvent || !this.relatedEvents.length) return false;
    
    // Find the main event (the one without parent_event_id)
    const mainEvent = this.relatedEvents.find(event => !event.parent_event_id);
    if (!mainEvent) return false;
    
    return this.hasUserParticipated(mainEvent);
  }

  get eventTitle(): string {
    if (!this.event?.description?.short_description?.['da']?.translation_text) {
      return 'Unavngivet Begivenhed';
    }
    return this.event.description.short_description['da'].translation_text;
  }

  get longDescription(): string {
    if (!this.event?.description?.long_description?.['da']?.translation_text) {
      return '';
    }
    return this.event.description.long_description['da'].translation_text;
  }

  get formattedDate(): string {
    if (!this.event?.start_date) return '';
    try {
      const start = new Date(this.event.start_date);
      const startDate = start.toLocaleDateString('da-DK');
      const startTime = start.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
      
      if (!this.event?.end_date) return startDate;
      
      const end = new Date(this.event.end_date);
      const endDate = end.toLocaleDateString('da-DK');
      const endTime = end.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
      
      // If dates are the same, only show end time
      if (startDate === endDate) {
        return startDate;
      } else {
        // If dates are different, show both full dates
        return `${startDate} - ${endDate}`;
      }
    } catch (error) {
      console.error('DunaEventsInformation: Error formatting dates:', error);
      return '';
    }
  }

  get formattedTime(): string {
    if (!this.event?.start_date) return '';
    try {
      const start = new Date(this.event.start_date);
      const startTime = start.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
      
      if (!this.event?.end_date) return startTime;
      
      const end = new Date(this.event.end_date);
      const endTime = end.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
      
      return `${startTime} - ${endTime}`;
    } catch (error) {
      console.error('DunaEventsInformation: Error formatting times:', error);
      return '';
    }
  }

  /** Tilmeldingsformular på event-siden (ikke i modal); skjules når tilmelding sker andetsteds. */
  get showParticipateForm(): boolean {
    if (!this.hasValidData || !this.event || this.isModal) {
      return false;
    }
    if (this.isSharedSeriesExtraDay) {
      return false;
    }
    if (this.isSubEvent && !this.sharesParticipantsAcrossSeries) {
      return false;
    }
    if (this.isSharedLinkedGroupSeries && !this.isLinkedGroupRegistrationAnchor) {
      return false;
    }
    return true;
  }

  get showFooter(): boolean {
    if (!this.isModal) return false; // Only show footer in modal mode
    
    // For sub-events with shared registration, registration happens on the main event only
    if (this.isSharedSeriesExtraDay) {
      return false;
    }

    // For sub-events without shared registration, only show if user participated in main
    if (this.isSubEvent) {
      return this.hasUserParticipatedInMainEvent();
    }

    // Linked group: registration on anchor only when participants are shared across series
    if (this.isSharedLinkedGroupSeries && !this.isLinkedGroupRegistrationAnchor) {
      return false;
    }
    
    // For main events, always show the button
    return true;
  }

  get footerButtonText(): string {
    return 'Gå til begivenhed';
  }

  get footerButtonIcon(): string {
    return 'fas fa-external-link-alt';
  }

  get isSubEvent(): boolean {
    if (this.event?.parent_event_id) {
      return true;
    }
    if (this.isSharedLinkedGroupSeries && !this.isLinkedGroupRegistrationAnchor) {
      return true;
    }
    return false;
  }

  /** Linked group where one registration covers all days — show series UI to users. */
  get isSharedLinkedGroupSeries(): boolean {
    return this.isLinkedGroupEvent && this.sharesParticipantsAcrossSeries;
  }

  get isSharedSeriesExtraDay(): boolean {
    if (!this.sharesParticipantsAcrossSeries) {
      return false;
    }
    if (this.isSharedLinkedGroupSeries) {
      return !this.isLinkedGroupRegistrationAnchor;
    }
    return this.isSubEvent;
  }

  get seriesRegistrationAnchorLabel(): string {
    if (this.isSharedLinkedGroupSeries) {
      return this.linkedGroupAnchorLabel;
    }
    if (this.isSubEvent && this.masterEventGuid) {
      const main = this.relatedEvents.find((e) => e.guid === this.masterEventGuid);
      if (main) {
        return this.getEventDate(main);
      }
    }
    return 'hovedbegivenheden';
  }

  get seriesExtraDayBadgeLabel(): string {
    return 'Ekstra dato';
  }

  get seriesRegistrationAnchorBadgeLabel(): string {
    return 'Tilmelding her';
  }

  get seriesExtraDayWarningTitle(): string {
    return 'Ekstra dato i serien';
  }

  get seriesExtraDayWarningMessage(): string {
    if (this.isSharedLinkedGroupSeries && !this.isLinkedGroupRegistrationAnchor) {
      return (
        'Tilmeld dig via første dag i serien for at deltage i hele forløbet. ' +
        'Serien har flere datoer — se oversigten nedenfor.'
      );
    }
    if (this.isSubEvent && this.sharesParticipantsAcrossSeries) {
      return (
        'Én tilmelding på hovedbegivenheden dækker også denne dato og de øvrige ekstra datoer i serien. ' +
        'Se alle datoer i oversigten nedenfor.'
      );
    }
    return '';
  }

  get isLinkedGroupEvent(): boolean {
    return !!(this.event?.linked_event_group_id);
  }

  /** True when one registration covers all days in a series (explicit opt-in). */
  get sharesParticipantsAcrossSeries(): boolean {
    return this.event?.series_share_participants === true;
  }

  get isLinkedGroupRegistrationAnchor(): boolean {
    if (!this.isSharedLinkedGroupSeries || !this.event) {
      return false;
    }
    if (this.event.series_primary === true) {
      return true;
    }
    if (this.event.series_primary === false) {
      return false;
    }
    if (this.relatedEvents.length > 0) {
      return this.relatedEvents[0].guid === this.event.guid;
    }
    return true;
  }

  get linkedGroupRegistrationAnchorGuid(): string | null {
    if (!this.isSharedLinkedGroupSeries) {
      return null;
    }
    const primary = this.relatedEvents.find((e) => e.series_primary === true);
    return primary?.guid || this.relatedEvents[0]?.guid || null;
  }

  get masterEventGuid(): string | null {
    if (this.isSharedLinkedGroupSeries) {
      return this.linkedGroupRegistrationAnchorGuid;
    }
    if (!this.isSubEvent) {
      return null;
    }
    return this.event?.parent_event_guid || null;
  }

  get subEventWarning(): string {
    if (!this.isSubEvent) return '';
    if (this.sharesParticipantsAcrossSeries) {
      return this.seriesExtraDayWarningMessage;
    }
    return 'Dette er en underbegivenhed. Du skal tilmelde dig hovedbegivenheden for at deltage.';
  }

  get linkedEventRegistrationWarning(): string {
    return this.seriesExtraDayWarningMessage;
  }

  get linkedGroupAnchorLabel(): string {
    const anchorGuid = this.linkedGroupRegistrationAnchorGuid;
    if (!anchorGuid) {
      return 'første dag';
    }
    const anchor = this.relatedEvents.find((e) => e.guid === anchorGuid);
    if (!anchor) {
      return 'første dag';
    }
    return this.getEventDate(anchor);
  }

  // Related events properties
  relatedEvents: EventDTO[] = [];
  isLoadingRelatedEvents = false;
  currentEventIndex = -1;

  get hasRelatedEvents(): boolean {
    return this.relatedEvents.length > 1;
  }

  get timelineTitle(): string {
    if (this.relatedEvents.length <= 1) return '';
    if (this.isSharedLinkedGroupSeries) {
      return 'Alle datoer i serien';
    }
    if (this.sharesParticipantsAcrossSeries) {
      return 'Alle datoer i serien';
    }
    return 'Begivenhedsserie';
  }

  navigateToEvent() {
    if (this.modalUuid && this.event?.guid) {
      // Close the modal using ModalService
      this.modalService.closeModal(this.modalUuid);
      // Wait a brief moment before navigating to ensure modal closes smoothly
      setTimeout(() => {
        this.router.navigate(['/event', this.event!.guid], { 
          queryParams: {},
          queryParamsHandling: '', // explicitly drop any existing query params
          replaceUrl: false 
        });
      }, 150);
    }
  }

  navigateToEditEvent() {
    if (!this.event?.guid) {
      return;
    }

    if (this.modalData?.openEditInNewTab) {
      // Close before opening the edit tab. Leaving the modal open is what made it
      // unclosable after the edit tab was closed (resume splash sat on top of it).
      if (this.modalUuid) {
        this.modalService.closeModal(this.modalUuid);
      } else {
        this.modalService.closeAllModals();
      }
      window.open(
        `${window.location.origin}/edit-event/${this.event.guid}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }

    if (this.modalUuid) {
      // Close the modal if we're in a modal
      this.modalService.closeModal(this.modalUuid);
      // Wait a brief moment before navigating
      setTimeout(() => {
        this.router.navigate(['/edit-event', this.event!.guid], {
          queryParams: {},
          queryParamsHandling: '', // explicitly drop any existing query params
          replaceUrl: false
        });
      }, 150);
    } else {
      // Direct navigation if we're not in a modal
      this.router.navigate(['/edit-event', this.event!.guid], {
        queryParams: {},
        queryParamsHandling: '', // explicitly drop any existing query params
        replaceUrl: false
      });
    }
  }

  navigateToMasterEvent() {
    if (this.masterEventGuid) {
      if (this.modalUuid) {
        // Close the modal if we're in a modal
        this.modalService.closeModal(this.modalUuid);
        // Wait a brief moment before navigating
        setTimeout(() => {
          this.router.navigate(['/event', this.masterEventGuid!], { 
            queryParams: {},
            queryParamsHandling: '', // explicitly drop any existing query params
            replaceUrl: false 
          });
        }, 150);
      } else {
        // Direct navigation if we're not in a modal
        this.router.navigate(['/event', this.masterEventGuid!], { 
          queryParams: {},
          queryParamsHandling: '', // explicitly drop any existing query params
          replaceUrl: false 
        });
      }
    }
  }

  // Related events methods
  private async loadRelatedEvents() {
    if (!this.event) return;
    if (this.isLoadingRelatedEvents) return; // Prevent multiple simultaneous calls
    
    this.isLoadingRelatedEvents = true;
    
    try {
      if (this.isSharedLinkedGroupSeries) {
        await this.loadLinkedGroupEvents();
      } else if (this.isSubEvent) {
        // If this is a sub-event, get the main event and all its sub-events
        await this.loadMainEventAndSubEvents();
      } else {
        // If this is a main event, get all its sub-events
        await this.loadSubEvents();
      }
      
      // Find current event index
      this.currentEventIndex = this.relatedEvents.findIndex(e => e.guid === this.event?.guid);
      
      // Trigger change detection since we're using OnPush strategy
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('Error loading related events:', error);
    } finally {
      this.isLoadingRelatedEvents = false;
      // Trigger change detection to update loading state
      this.cdr.detectChanges();
    }
  }

  private async loadLinkedGroupEvents() {
    if (!this.event?.linked_event_group_id) {
      return;
    }

    try {
      const guids = new Set<string>();
      if (this.event.guid) {
        guids.add(this.event.guid);
      }
      for (const subEvent of this.event.sub_events || []) {
        if (subEvent.guid) {
          guids.add(subEvent.guid);
        }
      }

      const loaded: EventDTO[] = [];
      for (const guid of guids) {
        const groupEvent = await this.eventsService.getEventByGuid(guid).pipe(take(1)).toPromise();
        if (groupEvent) {
          loaded.push(groupEvent);
        }
      }

      loaded.sort(
        (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      );
      this.relatedEvents = loaded;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading linked group events:', error);
    }
  }

  private async loadMainEventAndSubEvents() {
    if (!this.masterEventGuid) return;
    
    try {
      // Load the main event
      const mainEvent = await this.eventsService.getEventByGuid(this.masterEventGuid).pipe(take(1)).toPromise();
      if (mainEvent) {
        this.relatedEvents = [mainEvent];
        this.cdr.detectChanges(); // Trigger change detection after main event loads
        
        // Load sub-events using GUID
        const subEvents = await this.eventsService.getSubEventsByParentGuid(mainEvent.guid).pipe(take(1)).toPromise();
        if (subEvents) {
          this.relatedEvents.push(...subEvents);
          this.cdr.detectChanges(); // Trigger change detection after sub-events load
        }
      }
    } catch (error) {
      console.error('Error loading main event and sub-events:', error);
    }
  }

  private async loadSubEvents() {
    if (!this.event) return;
    
    try {
      // Start with the main event
      this.relatedEvents = [this.event];
      this.cdr.detectChanges(); // Trigger change detection after main event is set
      
      // Load sub-events using GUID
      const subEvents = await this.eventsService.getSubEventsByParentGuid(this.event.guid).pipe(take(1)).toPromise();
      if (subEvents) {
        this.relatedEvents.push(...subEvents);
        this.cdr.detectChanges(); // Trigger change detection after sub-events load
      }
    } catch (error) {
      console.error('Error loading sub-events:', error);
    }
  }

  getRelatedEventBadgeLabel(relatedEvent: EventDTO, isMain: boolean): string {
    if (isMain) {
      if (this.isSharedLinkedGroupSeries || (this.sharesParticipantsAcrossSeries && this.relatedEvents.length > 1)) {
        return this.seriesRegistrationAnchorBadgeLabel;
      }
      return 'Hovedbegivenhed';
    }
    if (this.isSharedLinkedGroupSeries || this.sharesParticipantsAcrossSeries) {
      return this.seriesExtraDayBadgeLabel;
    }
    return 'Underbegivenhed';
  }

  navigateToRelatedEvent(event: EventDTO) {
    if (this.modalUuid) {
      // Close the modal if we're in a modal
      this.modalService.closeModal(this.modalUuid);
      // Wait a brief moment before navigating
      setTimeout(() => {
        this.router.navigate(['/event', event.guid], { 
          queryParams: {},
          queryParamsHandling: '', // explicitly drop any existing query params
          replaceUrl: false 
        });
      }, 150);
    } else {
      // Direct navigation if we're not in a modal
      this.router.navigate(['/event', event.guid], { 
        queryParams: {},
        queryParamsHandling: '', // explicitly drop any existing query params
        replaceUrl: false 
      });
    }
  }

  getEventDate(event: EventDTO): string {
    try {
      const startDate = new Date(event.start_date);
      return startDate.toLocaleDateString('da-DK', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    } catch (error) {
      console.error('Error formatting event date:', error);
      return '';
    }
  }

  getEventTime(event: EventDTO): string {
    try {
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      
      const startTime = startDate.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit'
      });
      
      const endTime = endDate.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit'
      });
      
      return `${startTime} - ${endTime}`;
    } catch (error) {
      console.error('Error formatting event time:', error);
      return '';
    }
  }

  isCurrentEvent(event: EventDTO): boolean {
    return event.guid === this.event?.guid;
  }

  isMainEvent(event: EventDTO): boolean {
    if (this.isSharedLinkedGroupSeries) {
      if (event.series_primary === true) {
        return true;
      }
      if (this.relatedEvents.length > 0 && event.guid === this.relatedEvents[0].guid) {
        return event.series_primary !== false;
      }
      return false;
    }
    return !event.parent_event_id;
  }

  navigateToLinkedGroupAnchor() {
    const anchorGuid = this.linkedGroupRegistrationAnchorGuid;
    if (!anchorGuid) {
      return;
    }
    if (this.modalUuid) {
      this.modalService.closeModal(this.modalUuid);
      setTimeout(() => {
        this.router.navigate(['/event', anchorGuid], {
          queryParams: {},
          queryParamsHandling: '',
          replaceUrl: false,
        });
      }, 150);
    } else {
      this.router.navigate(['/event', anchorGuid], {
        queryParams: {},
        queryParamsHandling: '',
        replaceUrl: false,
      });
    }
  }

  shouldShowVisibilityLabel(visibility: string | undefined | null): boolean {
    return shouldShowEventVisibilityLabel(visibility, this.authService.isAuthenticated);
  }

  canEditEvent(): Observable<boolean> {
    // If no event, deny access
    if (!this.event?.guid) {
      return of(false);
    }

    if (!this.authService.isAuthenticated) {
      return of(false);
    }

    // Return cached result if available
    if (this.canEditEventCache$) {
      return this.canEditEventCache$;
    }

    // Use the permission evaluator with event-specific context
    const contextTemplate = `{
      "eventId": "${this.event.guid}",
      "eventOrganizationId": ${this.event.organization?.id || 'null'},
      "eventLocationGuid": "${this.event.predefined_location?.guid || ''}"
    }`;

    // Cache the result
    this.canEditEventCache$ = this.permissionEvaluatorService.evaluatePermission(
      'canEditEvent', 
      contextTemplate,
      { 
        eventId: this.event.guid,
        eventOrganizationId: this.event.organization?.id,
        eventLocationGuid: this.event.predefined_location?.guid
      },
      this.route
    ).pipe(
      take(1) // Only take the first result to avoid repeated calls
    );

    return this.canEditEventCache$;
  }

  get isCurrentUserInstructor(): boolean {
    return this.currentUserRole === 'instructor';
  }

  get isRegisteredParticipant(): boolean {
    const participant = this.currentUserParticipantInfo;
    if (!participant || participant.role === 'instructor') {
      return false;
    }
    const status = String(participant.status ?? '').toLowerCase();
    return (
      status !== 'waitlist' &&
      status !== 'not_participant' &&
      !isAfbudParticipationStatus(participant.status)
    );
  }

  get canViewEventFiles(): boolean {
    return this.isCurrentUserInstructor || this.userCanEditEvent || this.isRegisteredParticipant;
  }

  get showEventFileCategories(): boolean {
    return this.isCurrentUserInstructor || this.userCanEditEvent;
  }

  get isInsideEventFolder(): boolean {
    return this.eventFilesFolderStack.length > 0;
  }

  get participantEventFiles(): FileItemDTO[] {
    return this.eventFiles.filter((file) => this.getEventFileRole(file) !== 'instructor');
  }

  get instructorEventFiles(): FileItemDTO[] {
    return this.eventFiles.filter((file) => this.getEventFileRole(file) === 'instructor');
  }

  get participantEventFolders(): FileItemDTO[] {
    return this.eventFolders.filter((folder) => this.getEventFileRole(folder) !== 'instructor');
  }

  get instructorEventFolders(): FileItemDTO[] {
    return this.eventFolders.filter((folder) => this.getEventFileRole(folder) === 'instructor');
  }

  get visibleEventFiles(): FileItemDTO[] {
    if (this.isInsideEventFolder || this.showEventFileCategories) {
      return this.eventFiles;
    }
    return this.participantEventFiles;
  }

  get visibleEventFolders(): FileItemDTO[] {
    if (this.isInsideEventFolder || this.showEventFileCategories) {
      return this.eventFolders;
    }
    return this.participantEventFolders;
  }

  get hasAnyEventFiles(): boolean {
    if (this.showEventFileCategories && !this.isInsideEventFolder) {
      return (
        this.participantEventFiles.length > 0 ||
        this.instructorEventFiles.length > 0 ||
        this.participantEventFolders.length > 0 ||
        this.instructorEventFolders.length > 0
      );
    }
    return this.visibleEventFiles.length > 0 || this.visibleEventFolders.length > 0;
  }

  get eventFilesFolderTitle(): string {
    if (!this.eventFilesFolderStack.length) {
      return 'Filer';
    }
    return this.eventFilesFolderStack[this.eventFilesFolderStack.length - 1].name;
  }

  private getEventFileRole(file: FileItemDTO): 'participant' | 'instructor' {
    return file.event_role === 'instructor' ? 'instructor' : 'participant';
  }

  private refreshEventFilesAccess(): void {
    if (!this.event?.guid) {
      this.clearEventFiles();
      return;
    }

    if (!this.authService.isAuthenticated) {
      this.userCanEditEvent = false;
      this.clearEventFiles();
      this.cdr.detectChanges();
      return;
    }

    this.canEditEvent().pipe(take(1), takeUntil(this.destroy$)).subscribe((canEdit) => {
      this.userCanEditEvent = !!canEdit;
      if (this.canViewEventFiles) {
        void this.loadEventFiles();
      } else {
        this.clearEventFiles();
        this.cdr.detectChanges();
      }
    });
  }

  private clearEventFiles(): void {
    this.eventFiles = [];
    this.eventFolders = [];
    this.eventFilesFolderStack = [];
    this.isLoadingEventFiles = false;
  }

  private async loadEventFiles(parentFolder?: string): Promise<void> {
    if (!this.event?.guid) {
      return;
    }

    this.isLoadingEventFiles = true;
    this.cdr.detectChanges();

    try {
      const response = await this.mediaService.listFiles({
        scope_type: 'event',
        scope_id: this.event.guid,
        include_trash: false,
        parent_folder: parentFolder,
        event_role: this.showEventFileCategories ? undefined : 'participant',
      });
      const items = response?.files ?? [];
      this.eventFolders = items.filter((item) => item.is_folder);
      this.eventFiles = items.filter((item) => !item.is_folder);
    } catch (error) {
      console.error('DunaEventsInformation: Failed to load event files', error);
      this.eventFiles = [];
      this.eventFolders = [];
    } finally {
      this.isLoadingEventFiles = false;
      this.cdr.detectChanges();
    }
  }

  async openEventFolder(folder: FileItemDTO): Promise<void> {
    this.eventFilesFolderStack = [
      ...this.eventFilesFolderStack,
      {
        name: folder.display_name || folder.original_name || 'Mappe',
        obfuscatedName: folder.obfuscated_name,
      },
    ];
    await this.loadEventFiles(folder.obfuscated_name);
  }

  async goBackEventFolder(): Promise<void> {
    const nextStack = this.eventFilesFolderStack.slice(0, -1);
    this.eventFilesFolderStack = nextStack;
    const parent = nextStack.length ? nextStack[nextStack.length - 1].obfuscatedName : undefined;
    await this.loadEventFiles(parent);
  }

  previewEventFile(file: FileItemDTO): void {
    if (!this.isEventFileReady(file)) {
      return;
    }
    window.open(`/media/${file.obfuscated_name}`, '_blank', 'noopener,noreferrer');
  }

  async downloadEventFile(file: FileItemDTO): Promise<void> {
    if (!this.isEventFileReady(file)) {
      return;
    }

    try {
      const blob = await this.mediaService.downloadFile(file.obfuscated_name);
      const url = URL.createObjectURL(blob);
      const link = this.document.createElement('a');
      link.href = url;
      link.download = file.display_name || file.original_name || 'fil';
      this.document.body.appendChild(link);
      link.click();
      this.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('DunaEventsInformation: Failed to download event file', error);
    }
  }

  isEventFileReady(file: FileItemDTO): boolean {
    return file.status === 'ready' || file.status === 'completed' || !file.status;
  }

  getEventFileIcon(file: FileItemDTO): string {
    if (file.is_folder) {
      return 'folder';
    }
    if (file.is_video) {
      return 'videocam';
    }
    const mime = file.mime_type || '';
    if (mime.includes('pdf')) {
      return 'picture_as_pdf';
    }
    if (mime.startsWith('image/')) {
      return 'image';
    }
    if (mime.includes('spreadsheet') || mime.includes('excel')) {
      return 'table_chart';
    }
    if (mime.includes('word') || mime.includes('document')) {
      return 'description';
    }
    return 'insert_drive_file';
  }

  formatEventFileSize(bytes: number): string {
    if (!bytes) {
      return '';
    }
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${Math.round((bytes / Math.pow(1024, i)) * 10) / 10} ${units[i]}`;
  }

  canViewWaitlistInfo(): Observable<boolean> {
    return this.userRoles$.pipe(
      map(roles => {
        // Only orgbruger, orgadmin, and superadmin can view waitlist information
        const allowedRoles = ['superadmin', 'orgadmin', 'orgbruger'];
        return roles.some(role => allowedRoles.includes(role.toLowerCase()));
      })
    );
  }

  // Type guard methods for polymorphic item details
  isSubTypeDTO(obj: any): obj is SubTypeDTO {
    return obj && obj.hasOwnProperty('descriptions') && obj.hasOwnProperty('subtype_id');
  }

  isGoalDTO(obj: any): obj is GoalDTO {
    return obj && obj.hasOwnProperty('descriptions') && obj.hasOwnProperty('goal_id');
  }

  isModuleDTO(obj: any): obj is ModuleDTO {
    return obj && obj.hasOwnProperty('descriptions') && obj.hasOwnProperty('module_id');
  }

  isEducationalPathDTO(obj: any): obj is EducationalPathDTO {
    return obj && obj.hasOwnProperty('description') && obj.hasOwnProperty('path_id');
  }

  toggleModule(item: any): void {
    const id = item.subtype_id || item.module_id || item.goal_id || item.path_id;
    if (id) {
      this.expandedModules[id] = !this.expandedModules[id];
      this.cdr.detectChanges();
    }
  }

  getModuleDescription(item: any): string {
    if (!item?.descriptions) return '';
    
    // Try to get Danish description first
    const daDescription = item.descriptions['da']?.translation_text;
    if (daDescription) return daDescription;
    
    // Fall back to first available description
    const firstDescription = Object.values(item.descriptions)[0] as any;
    return firstDescription?.translation_text || '';
  }

  get instructors(): EventParticipantDTO[] {
    if (!this.event?.participants) return [];
    return this.event.participants.filter(participant => 
      participant.role === 'instructor'
    );
  }

    get currentUserParticipantInfo(): EventParticipantDTO | null {
    if (!this.event?.participants) {
      return null;
    }
    
    if (!this.currentUserUuid) {
      return null;
    }
    
    
    const participant = this.event.participants.find(participant => {
      const matches = participant.user.uuid === this.currentUserUuid;
      return matches;
    }) || null;
    
    return participant;
  }

  get isCurrentUserParticipating(): boolean {
    return this.currentUserParticipantInfo !== null;
  }

  get currentUserRole(): string | null {
    return this.currentUserParticipantInfo?.role || null;
  }

  get currentUserStatus(): string | null {
    return this.currentUserParticipantInfo?.status || null;
  }

  get roomDescription(): string {
    if (this.event?.predefined_room?.description) {
      return this.event.predefined_room.description;
    }
    return '';
  }

  getParticipantStatusText(): string {
    if (!this.isCurrentUserParticipating) return '';
    
    const role = this.currentUserRole;
    const status = this.currentUserStatus;
    
    switch (status) {
      case 'pending':
        return `Du er tilmeldt som ${this.getRoleDisplayName(role)}`;
      case 'shownup':
        return `Du deltager som ${this.getRoleDisplayName(role)}`;
      case 'waitlist':
        return `Du er på venteliste som ${this.getRoleDisplayName(role)}`;
      case 'completed':
        return `Du har gennemført som ${this.getRoleDisplayName(role)}`;
      case 'requires_attention':
        return `Din tilmelding som ${this.getRoleDisplayName(role)} kræver opmærksomhed`;
      default:
        return `Du er tilmeldt som ${this.getRoleDisplayName(role)}`;
    }
  }

  getParticipantStatusIcon(): string {
    if (!this.isCurrentUserParticipating) return '';
    
    const status = this.currentUserStatus;
    
    switch (status) {
      case 'pending':
        return 'fas fa-clock';
      case 'shownup':
        return 'fas fa-check-circle';
      case 'waitlist':
        return 'fas fa-hourglass-half';
      case 'completed':
        return 'fas fa-trophy';
      case 'requires_attention':
        return 'fas fa-exclamation-triangle';
      default:
        return 'fas fa-user-check';
    }
  }

  getParticipantStatusClass(): string {
    if (!this.isCurrentUserParticipating) return '';
    
    const status = this.currentUserStatus;
    
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'shownup':
        return 'status-confirmed';
      case 'waitlist':
        return 'status-waitlist';
      case 'completed':
        return 'status-completed';
      case 'requires_attention':
        return 'status-attention';
      default:
        return 'status-default';
    }
  }

  private getRoleDisplayName(role: string | null): string {
    switch (role) {
      case 'instructor':
        return 'instruktør';
      case 'assistant':
        return 'assistent';
      case 'attendee':
      default:
        return 'deltager';
    }
  }

  // QR Code methods
  showQRCode(): void {
    if (!this.event?.guid) {
      console.error('DunaEventsInformation: Cannot generate QR code - no event GUID available');
      return;
    }

    // Generate the event URL - always use https://www.duna.dk/
    this.eventQRData = `https://www.duna.dk/event/${this.event.guid}`;
    
    this.showQRModal = true;
    this.linkCopied = false;
    this.cdr.detectChanges();
    setTimeout(() => this.moveQrModalToBody(), 0);
  }

  closeQRCode(): void {
    this.removeQrModalFromBody();
    this.showQRModal = false;
    this.linkCopied = false;
    this.cdr.detectChanges();
  }

  copyEventLink(): void {
    if (!this.eventQRData) return;

    navigator.clipboard?.writeText(this.eventQRData).then(() => {
      this.linkCopied = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.linkCopied = false;
        this.cdr.detectChanges();
      }, 2000);
    });
  }

  private moveQrModalToBody(): void {
    const el = this.qrModalOverlay?.nativeElement;
    if (el && el.parentElement !== this.document.body) {
      this.renderer.appendChild(this.document.body, el);
    }
  }

  private removeQrModalFromBody(): void {
    const el = this.qrModalOverlay?.nativeElement;
    if (el?.parentElement === this.document.body) {
      this.renderer.removeChild(this.document.body, el);
    }
  }

  navigateToLogin(): void {
    window.location.href = '/login';
  }

  private setupEventRedirect(): void {
    if (!this.event?.guid) {
      console.warn('DunaEventsInformation: Cannot setup redirect - no event GUID available');
      return;
    }

    // Set the redirect URL in sessionStorage for the login component to use
    const eventUrl = `/event/${this.event.guid}`;
    sessionStorage.setItem('redirectUrl', eventUrl);
  }
}
